/**
 * reCAPTCHA v2 (checkbox) and v3 (invisible) composable.
 * Loads the script only when VITE_RECAPTCHA_SITE_KEY is set.
 * VITE_RECAPTCHA_VERSION: 'v2' (default) or 'v3'. v3 = invisible, no widget; use executeToken() on submit.
 * When VITE_RECAPTCHA_SITE_KEY is set, reCAPTCHA is used (dev or prod). When unset, no script loads and no token is sent.
 * To test reCAPTCHA in local dev, set the keys and RECAPTCHA_BYPASS_DEV=false on the API.
 */

import { ref, computed } from 'vue'
import { allowsThirdPartySync } from '@/utils/sitePrefsStorage'

const RECAPTCHA_SCRIPT_URL = 'https://www.google.com/recaptcha/api.js'
const SCRIPT_LOAD_PROMISE_KEY = '__rms_recaptcha_load_promise'
const ONLOAD_CALLBACK_NAME = '__rms_recaptcha_onload'

/** Use reCAPTCHA when site key is set (dev and prod). No bypass so you can test with keys in local. */
const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY
const key = (typeof siteKey === 'string' && siteKey.trim()) ? siteKey.trim() : undefined

const version = (() => {
  const v = import.meta.env.VITE_RECAPTCHA_VERSION
  return (v === 'v3' || v === 'v2') ? v : 'v2'
})()

const isReady = ref(false)
let widgetId = null

/**
 * Load the reCAPTCHA script. v2: render=explicit. v3: render=siteKey.
 * @returns {Promise<void>}
 */
function loadScript() {
  if (typeof window === 'undefined') return Promise.resolve()
  if (!allowsThirdPartySync()) {
    return Promise.reject(new Error('rms_cookie_no_third_party'))
  }
  const hasGrecaptcha = window.grecaptcha && (version === 'v3' ? window.grecaptcha.execute : window.grecaptcha.render)
  if (hasGrecaptcha) {
    isReady.value = true
    return Promise.resolve()
  }
  if (window[SCRIPT_LOAD_PROMISE_KEY]) return window[SCRIPT_LOAD_PROMISE_KEY]
  const renderParam = version === 'v3' ? key : 'explicit'
  window[SCRIPT_LOAD_PROMISE_KEY] = new Promise((resolve, reject) => {
    window[ONLOAD_CALLBACK_NAME] = () => {
      isReady.value = true
      resolve()
    }
    const script = document.createElement('script')
    script.src = `${RECAPTCHA_SCRIPT_URL}?onload=${ONLOAD_CALLBACK_NAME}&render=${renderParam}`
    script.async = true
    script.defer = true
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA script'))
    document.head.appendChild(script)
  })
  return window[SCRIPT_LOAD_PROMISE_KEY]
}

/**
 * Render the reCAPTCHA v2 checkbox into the given container. No-op for v3.
 * @param {HTMLElement} container
 * @returns {Promise<number>}
 */
function renderWidget(container) {
  if (!key) return Promise.reject(new Error('reCAPTCHA site key not configured'))
  if (version === 'v3') return Promise.resolve(-1)
  return loadScript().then(() => {
    if (!container || !window.grecaptcha?.render) {
      return Promise.reject(new Error('reCAPTCHA not ready or invalid container'))
    }
    widgetId = window.grecaptcha.render(container, { sitekey: key, theme: 'light' })
    return widgetId
  })
}

/**
 * Get the current response token from the v2 widget. Empty for v3 (use executeToken instead).
 * @returns {string}
 */
function getToken() {
  if (version === 'v3') return ''
  if (typeof window === 'undefined' || !window.grecaptcha || widgetId == null) return ''
  return window.grecaptcha.getResponse(widgetId) || ''
}

/**
 * Execute reCAPTCHA v3 and return a new token. For v2 returns getToken() (sync).
 * v3 requires grecaptcha.ready() before execute() per Google's docs.
 * @param {string} [action='feedback']
 * @returns {Promise<string>}
 */
function executeToken(action = 'feedback') {
  if (!key) return Promise.resolve('')
  if (version === 'v2') return Promise.resolve(getToken())
  return loadScript()
    .then(() => {
      const g = window.grecaptcha
      if (!g?.execute) return ''
      return new Promise((resolve) => {
        const run = () => {
          g.execute(key, { action })
            .then((token) => resolve(token || ''))
            .catch(() => resolve(''))
        }
        if (typeof g.ready === 'function') {
          g.ready(run)
        } else {
          run()
        }
      })
    })
    .catch(() => '')
}

/**
 * Reset the v2 widget. No-op for v3.
 */
function reset() {
  if (version !== 'v2') return
  if (typeof window !== 'undefined' && window.grecaptcha && widgetId != null) {
    window.grecaptcha.reset(widgetId)
  }
}

export function useRecaptcha() {
  const recaptchaEnabled = computed(() => !!key)
  const recaptchaVersion = computed(() => version)
  const recaptchaIsV3 = computed(() => version === 'v3')
  return {
    recaptchaEnabled,
    recaptchaVersion,
    recaptchaIsV3,
    isReady,
    loadScript: key ? loadScript : () => Promise.resolve(),
    renderWidget: key ? renderWidget : () => Promise.reject(new Error('reCAPTCHA not configured')),
    getToken: key ? getToken : () => '',
    executeToken: key ? executeToken : () => Promise.resolve(''),
    reset: key ? reset : () => {},
  }
}
